import { ParsedMermaidNode, ParsedMermaidEdge, MermaidNodeShape } from './types';

/**
 * Utility functions for Mermaid adapter
 */

/**
 * Generate a unique ID for a node if one doesn't exist
 */
export function generateNodeId(prefix: string = 'node'): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Sanitize node/edge IDs to ensure they're valid
 */
export function sanitizeId(id: string): string {
  return id.replace(/[^A-Za-z0-9_]/g, '_');
}

/**
 * Extract label from Mermaid node definition
 */
export function extractLabel(nodeDef: string): string {
  // Try to extract label from various formats
  const labelMatch = nodeDef.match(/\[([^\]]+)\]|\(\(([^\)]+)\)\)|\(([^\)]+)\)|\{([^}]+)\}/);
  return labelMatch ? (labelMatch[1] || labelMatch[2] || labelMatch[3] || labelMatch[4] || '').trim() : '';
}

/**
 * Determine node shape from Mermaid syntax
 */
export function detectNodeShape(nodeDef: string): MermaidNodeShape {
  if (nodeDef.includes('((')) return 'circle';
  if (nodeDef.includes('{')) return 'diamond';
  if (nodeDef.includes('(') && !nodeDef.includes('((')) return 'rounded';
  if (nodeDef.includes('[[')) return 'subroutine';
  if (nodeDef.includes('[(]')) return 'cylinder';
  if (nodeDef.includes('/')) return 'parallelogram';
  if (nodeDef.includes('\\')) return 'parallelogram-alt';
  return 'rectangle';
}

/**
 * Calculate optimal node dimensions based on label
 */
export function calculateNodeDimensions(
  label: string,
  shape: MermaidNodeShape = 'rectangle'
): { width: number; height: number } {
  const baseWidth = 160;
  const baseHeight = 80;
  const labelLines = label.split('\n').length;
  const minHeight = Math.max(baseHeight, labelLines * 24 + 32);

  switch (shape) {
    case 'circle':
      const size = Math.max(baseWidth, baseHeight);
      return { width: size, height: size };
    case 'diamond':
      return { width: baseWidth + 20, height: baseHeight + 20 };
    default:
      return { width: baseWidth, height: minHeight };
  }
}

/**
 * Get default color for link type
 */
export function getLinkColorForType(
  type: string,
  theme: boolean = true
): string {
  const lightColors: Record<string, string> = {
    async: '#3B82F6',
    sync: '#10B981',
    inheritance: '#8B5CF6',
    transition: '#F59E0B',
    relationship: '#EF4444',
    default: '#6B7280',
  };

  const darkColors: Record<string, string> = {
    async: '#60A5FA',
    sync: '#34D399',
    inheritance: '#A78BFA',
    transition: '#FBBF24',
    relationship: '#F87171',
    default: '#9CA3AF',
  };

  const colors = theme ? lightColors : darkColors;
  return colors[type] || colors.default;
}

/**
 * Validate Mermaid specification format
 */
export function validateMermaidSpec(spec: string): { valid: boolean; error?: string } {
  if (!spec || typeof spec !== 'string') {
    return { valid: false, error: 'Specification must be a non-empty string' };
  }

  const trimmed = spec.trim();
  if (!trimmed) {
    return { valid: false, error: 'Specification cannot be empty' };
  }

  // Check for basic Mermaid diagram type indicators
  const firstLine = trimmed.split(/\r?\n/)[0].trim().toLowerCase();
  const validTypes = [
    'flowchart',
    'graph',
    'sequencediagram',
    'classdiagram',
    'statediagram',
    'erdiagram',
  ];

  const hasValidType = validTypes.some(type => firstLine.startsWith(type));

  if (!hasValidType) {
    return {
      valid: false,
      error: 'Specification must start with a valid Mermaid diagram type (flowchart, graph, sequenceDiagram, etc.)',
    };
  }

  return { valid: true };
}

/**
 * Format parse error for display
 */
export function formatParseError(error: { message: string; line?: number; column?: number }): string {
  let formatted = error.message;
  if (error.line !== undefined) {
    formatted += ` (Line ${error.line}`;
    if (error.column !== undefined) {
      formatted += `, Column ${error.column}`;
    }
    formatted += ')';
  }
  return formatted;
}



